import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Ruler, ShieldCheck } from "lucide-react";

const BG = "https://images.unsplash.com/photo-1624213012413-fda54df1810f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzgxMjk0MzQ2fDA&ixlib=rb-4.1.0&q=85";

export default function LoginPage() {
  const { user, loading, login, register, completeLogin } = useAuth();
  const [mode, setMode] = useState("login"); // login | register | change
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [pwd, setPwd] = useState({ newPassword: "", confirm: "" });
  const [tempUser, setTempUser] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  if (!loading && user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setNotice(""); setBusy(true);
    try {
      if (mode === "login") {
        const data = await login(form.email, form.password);
        if (data.user?.must_change_password) {
          setTempUser(data.user);
          setMode("change");
        }
      } else if (mode === "forgot") {
        const { data } = await api.post("/auth/forgot-password", { email: form.email });
        setNotice(data.message);
        setMode("login");
      } else {
        const data = await register(form.name, form.email, form.password);
        if (data.pending) {
          setNotice(data.message);
          setMode("login");
          setForm({ ...form, password: "" });
        }
      }
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitNewPassword = async (e) => {
    e.preventDefault();
    setError("");
    if (pwd.newPassword.length < 6) { setError("Password must be at least 6 characters"); return; }
    if (pwd.newPassword !== pwd.confirm) { setError("Passwords do not match"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.password, new_password: pwd.newPassword });
      completeLogin({ ...tempUser, must_change_password: false });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const inputCls = "w-full border border-zinc-300 bg-white px-3 py-2.5 text-sm rounded-sm focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] outline-none";

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex items-center justify-center p-8 bg-[#FDFDFD]">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 bg-[#002FA7] flex items-center justify-center rounded-sm">
              <Ruler size={16} className="text-white" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-semibold tracking-tight">SCOPEWORKS</span>
          </div>

          {mode === "change" ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <ShieldCheck size={22} className="text-[#002FA7]" strokeWidth={1.5} />
                <h1 className="font-heading text-3xl font-medium tracking-tighter">Set a new password</h1>
              </div>
              <p className="text-sm text-zinc-500 mb-8">
                You signed in with a temporary password. Choose a new one to continue.
              </p>
              <form onSubmit={submitNewPassword} className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block">New password</label>
                  <input data-testid="new-password-input" type="password" className={inputCls}
                    value={pwd.newPassword} onChange={(e) => setPwd({ ...pwd, newPassword: e.target.value })} required />
                </div>
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block">Confirm new password</label>
                  <input data-testid="confirm-password-input" type="password" className={inputCls}
                    value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} required />
                </div>
                {error && <p data-testid="login-error" className="text-xs text-red-600">{error}</p>}
                <button data-testid="change-password-submit" disabled={busy}
                  className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm transition-colors disabled:opacity-50">
                  {busy ? "Please wait…" : "Set password & continue"}
                </button>
              </form>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-medium tracking-tighter mb-1">
                {mode === "login" ? "Sign in" : mode === "register" ? "Create account" : "Reset password"}
              </h1>
              <p className="text-sm text-zinc-500 mb-8">
                {mode === "forgot" ? "Enter your email and we'll send you a reset link." : "Estimation & resource planning workspace"}
              </p>
              {notice && (
                <div data-testid="register-pending-notice" className="mb-6 border border-amber-300 bg-amber-50 text-amber-800 text-xs px-3 py-2.5 rounded-sm">
                  {notice}
                </div>
              )}
              <form onSubmit={submit} className="space-y-4">
                {mode === "register" && (
                  <div>
                    <label className="text-xs font-medium text-zinc-700 mb-1 block">Name</label>
                    <input data-testid="register-name-input" className={inputCls} value={form.name} onChange={set("name")} required />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-zinc-700 mb-1 block">Email</label>
                  <input data-testid="login-email-input" type="email" className={inputCls} value={form.email} onChange={set("email")} placeholder="you@resolvetech.com" required />
                </div>
                {mode !== "forgot" && (
                  <div>
                    <label className="text-xs font-medium text-zinc-700 mb-1 block">Password</label>
                    <input data-testid="login-password-input" type="password" className={inputCls} value={form.password} onChange={set("password")} required />
                    {mode === "login" && (
                      <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); setNotice(""); }}
                        className="mt-1.5 text-xs text-zinc-500 hover:text-[#002FA7]">
                        Forgot password?
                      </button>
                    )}
                  </div>
                )}
                {error && <p data-testid="login-error" className="text-xs text-red-600">{error}</p>}
                <button data-testid="login-submit-button" disabled={busy}
                  className="w-full bg-[#002FA7] hover:bg-[#002280] text-white text-sm font-medium py-2.5 rounded-sm transition-colors disabled:opacity-50">
                  {busy ? "Please wait…" : mode === "login" ? "Sign in" : mode === "register" ? "Register" : "Send reset link"}
                </button>
              </form>
              {mode === "forgot" ? (
                <button data-testid="toggle-auth-mode" onClick={() => { setMode("login"); setError(""); }}
                  className="mt-6 text-xs text-zinc-500 hover:text-[#002FA7]">
                  Back to sign in
                </button>
              ) : (
                <button data-testid="toggle-auth-mode" onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); setNotice(""); }}
                  className="mt-6 text-xs text-zinc-500 hover:text-[#002FA7]">
                  {mode === "login" ? "No account? Register (requires a @resolvetech.com email)" : "Already registered? Sign in"}
                </button>
              )}
            </>
          )}
        </div>
      </div>
      <div className="hidden lg:block w-[42%] relative">
        <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-[#002FA7]/40" />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="text-[10px] uppercase tracking-[0.2em] mb-2 opacity-80">Estimation OS</p>
          <p className="font-heading text-2xl tracking-tight leading-snug">
            BRD in. Hybrid T-shirt + WBS estimate, weekly resource load and pricing out.
          </p>
        </div>
      </div>
    </div>
  );
}
